import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, IconButton, Snackbar, Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CategoryIcon from '@mui/icons-material/Category';
import { adminFetch } from '../../lib/adminAuth';

const DEFAULT_NAMES = [
  'Cafés', 'Restaurants', 'Hair Salons', 'Nail Salons', 'Gyms',
  'Car Washes', 'Pet Groomers', 'Massage Studios', 'Beauty Bars',
  'Bakeries', 'Juice Bars', 'Barbershops', 'Other',
];
const DEFAULTS = DEFAULT_NAMES.map(name => ({ name, firstReminderDays: 7 }));

// Tolerate either the object shape or the legacy plain-string shape.
const normalize = (list) => (Array.isArray(list) ? list : []).map((c) =>
  typeof c === 'string'
    ? { name: c, firstReminderDays: 7 }
    : { name: c.name || '', firstReminderDays: c.firstReminderDays ?? 7 }
);

export default function AdminBusinessCategories() {
  const [categories, setCategories] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch('/api/admin/business-categories');
        const data = await res.json();
        if (data && Array.isArray(data.categories) && data.categories.length > 0) {
          setCategories(normalize(data.categories));
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const updateName = (i, val) => setCategories(prev => prev.map((c, idx) => (idx === i ? { ...c, name: val } : c)));
  const updateDays = (i, val) => setCategories(prev => prev.map((c, idx) => (idx === i ? { ...c, firstReminderDays: val } : c)));
  const remove = (i) => setCategories(prev => prev.filter((_, idx) => idx !== i));
  const add = () => setCategories(prev => [...prev, { name: '', firstReminderDays: 7 }]);
  const move = (i, dir) => setCategories(prev => {
    const a = [...prev];
    [a[i], a[i + dir]] = [a[i + dir], a[i]];
    return a;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = categories
        .map(c => ({
          name: (c.name || '').trim(),
          firstReminderDays: Math.max(1, Math.round(Number(c.firstReminderDays) || 7)),
        }))
        .filter(c => c.name);
      const res = await adminFetch('/api/admin/business-categories', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categories: cleaned }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data && Array.isArray(data.categories)) setCategories(normalize(data.categories));
      setSnack({ open: true, message: 'Business categories saved!', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Business Categories</Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>
            One shared list — powers the landing page marquee, the registration form and the vendor Store Settings dropdown
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#16213e' } }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CategoryIcon sx={{ color: '#ffb71b' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Categories</Typography>
          <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>({categories.length})</Typography>
        </Box>

        {/* Column headers */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, flex: 1 }}>Category</Typography>
          <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, width: 170 }}>First reminder (days)</Typography>
          <Box sx={{ width: 120 }} />
        </Box>

        {categories.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField value={item.name} onChange={e => updateName(i, e.target.value)} size="small" fullWidth placeholder="Category name" />
            <TextField
              value={item.firstReminderDays}
              onChange={e => updateDays(i, e.target.value)}
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              sx={{ width: 170 }}
            />
            <Box sx={{ width: 120, display: 'flex' }}>
              <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => move(i, 1)} disabled={i === categories.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => remove(i)} sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ textTransform: 'none', mt: 1 }}>Add Category</Button>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
